import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Shield, AlertTriangle, CheckCircle, XCircle, Clock, Zap,
  TrendingDown, TrendingUp, BarChart3, Scale, FileText, ChevronDown,
  ChevronRight, Eye, RefreshCw, Filter
} from 'lucide-react';
import { cn } from '../lib/utils';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface WhatIfScenario {
  type: string;
  changePercent: number;
}

interface WhatIfImpact {
  assetClass: string;
  currentValue: number;
  projectedValue: number;
  impact: number;
  impactPercent: number;
}

interface WhatIfResult {
  portfolioId: number;
  portfolioName: string;
  scenarioType: string;
  inputChange: number;
  currentValue: number;
  projectedValue: number;
  totalImpact: number;
  totalImpactPercent: number;
  impacts: WhatIfImpact[];
}

interface OverrideRecord {
  id: number;
  tenantId: number;
  ruleId: number;
  tradeId: number;
  orderId: string | null;
  requestedBy: string;
  requestedAt: string;
  justification: string;
  currentValue: number;
  limitValue: number;
  breachAmount: number;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'EXPIRED';
  reviewedBy: string | null;
  reviewedAt: string | null;
  reviewNotes: string | null;
  expiresAt: string | null;
  createdAt: string;
}

interface RegulatoryRule {
  id?: number;
  hasEvaluator?: boolean;
  ruleKey: string;
  ruleName: string;
  ruleType: string;
  description: string;
  thresholdValue: string;
  breachAction: string;
  severity: string;
  enabled: boolean;
  ruleScope: string;
  source: string;
}

interface Portfolio {
  id: number;
  portfolioName: string;
}

interface PreClearTrade {
  id: number;
  transactionType: string;
  securityName: string;
  securityId: number;
  portfolioName: string;
  portfolioId: number;
  amount: number;
  price: number;
  quantity: number;
  status: string;
  tradeDate: string;
  notes: string;
  createdAt: string;
}

// ---------------------------------------------------------------------------
// Styled Components
// ---------------------------------------------------------------------------

const Container = styled.div`
  min-height: calc(100vh - 64px);
  background: transparent;
`;

const TabsContainer = styled.div`
  background: linear-gradient(to bottom, rgba(15, 23, 42, 0.5) 0%, transparent 100%);
  padding: 1.5rem 2rem 0;
`;

const TabsWrapper = styled.div`
  display: flex;
  gap: 4px;
  max-width: 1800px;
  margin: 0 auto;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
`;

const ContentContainer = styled.div`
  max-width: 1800px;
  margin: 0 auto;
  padding: 2rem;
`;

const GlassCard = styled.div`
  background: rgba(30, 41, 59, 0.5);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  overflow: hidden;
`;

// ---------------------------------------------------------------------------
// Tab Component
// ---------------------------------------------------------------------------

const ComplianceTab: React.FC<{
  icon: React.ReactNode;
  label: string;
  badge?: number;
  active: boolean;
  onClick: () => void;
}> = ({ icon, label, badge, active, onClick }) => (
  <button
    onClick={onClick}
    className={cn(
      "group flex items-center gap-2.5 px-6 py-3 text-sm font-semibold transition-all duration-200 relative",
      active ? "text-white" : "text-slate-400 hover:text-slate-200"
    )}
  >
    <span className={cn(
      "transition-transform duration-200 group-hover:scale-110",
      active ? "text-amber-400" : "text-slate-500 group-hover:text-slate-300"
    )}>
      {icon}
    </span>
    {label}
    {badge != null && badge > 0 && (
      <span className="ml-1.5 px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-red-500/20 text-red-400 border border-red-500/30">
        {badge}
      </span>
    )}
    {active && (
      <motion.div
        layoutId="compliance-nav-indicator"
        className="absolute bottom-0 left-0 right-0 h-0.5 bg-amber-500 shadow-[0_0_10px_rgba(245,158,11,0.5)]"
      />
    )}
  </button>
);

// ---------------------------------------------------------------------------
// Helper: format currency
// ---------------------------------------------------------------------------

const fmtCurrency = (v: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v);

const fmtPct = (v: number, digits = 2) => `${v >= 0 ? '+' : ''}${v.toFixed(digits)}%`;

// ---------------------------------------------------------------------------
// Tab 1: What-If Scenarios
// ---------------------------------------------------------------------------

const WhatIfTab: React.FC = () => {
  const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
  const [selectedPortfolio, setSelectedPortfolio] = useState<number | null>(null);
  const [scenario, setScenario] = useState<WhatIfScenario>({ type: 'MARKET_DROP', changePercent: -10 });
  const [result, setResult] = useState<WhatIfResult | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get('/api/dimensions/portfolios').then(res => {
      const list = res.data.content ?? res.data;
      setPortfolios(Array.isArray(list) ? list.slice(0, 50) : []);
      if (Array.isArray(list) && list.length > 0) setSelectedPortfolio(list[0].id);
    }).catch(() => {});
  }, []);

  const runScenario = async () => {
    if (!selectedPortfolio) return;
    setLoading(true);
    try {
      const res = await axios.post(`/api/portfolio-health/portfolio/${selectedPortfolio}/what-if`, scenario);
      setResult(res.data);
    } catch { setResult(null); }
    setLoading(false);
  };

  return (
    <div className="space-y-6">
      {/* Scenario Form */}
      <GlassCard className="p-6">
        <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
          <Zap className="w-5 h-5 text-amber-400" />
          Scenario Configuration
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Portfolio</label>
            <select
              value={selectedPortfolio ?? ''}
              onChange={e => setSelectedPortfolio(Number(e.target.value))}
              className="w-full px-4 py-2.5 bg-slate-900 border border-white/10 rounded-lg text-white text-sm"
            >
              {portfolios.map(p => (
                <option key={p.id} value={p.id}>{p.portfolioName}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Scenario Type</label>
            <select
              value={scenario.type}
              onChange={e => setScenario({ ...scenario, type: e.target.value })}
              className="w-full px-4 py-2.5 bg-slate-900 border border-white/10 rounded-lg text-white text-sm"
            >
              <option value="MARKET_DROP">Market Drop</option>
              <option value="MARKET_RISE">Market Rise</option>
              <option value="RATE_SHOCK">Interest Rate Shock</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">
              {scenario.type === 'RATE_SHOCK' ? 'Change (bps)' : 'Change (%)'}
            </label>
            <input
              type="number"
              value={scenario.changePercent}
              onChange={e => setScenario({ ...scenario, changePercent: parseFloat(e.target.value) || 0 })}
              className="w-full px-4 py-2.5 bg-slate-900 border border-white/10 rounded-lg text-white text-sm"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={runScenario}
              disabled={loading || !selectedPortfolio}
              className="w-full px-6 py-2.5 bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white font-bold rounded-lg transition-all flex items-center justify-center gap-2"
            >
              {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
              Run Scenario
            </button>
          </div>
        </div>
      </GlassCard>

      {/* Results */}
      {result && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <GlassCard className="p-5 border-l-4 border-l-blue-500">
              <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Current Value</div>
              <div className="text-xl font-bold text-white">{fmtCurrency(result.currentValue)}</div>
            </GlassCard>
            <GlassCard className="p-5 border-l-4 border-l-amber-500">
              <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Projected Value</div>
              <div className="text-xl font-bold text-white">{fmtCurrency(result.projectedValue)}</div>
            </GlassCard>
            <GlassCard className={cn("p-5 border-l-4", result.totalImpact < 0 ? "border-l-red-500" : "border-l-emerald-500")}>
              <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Total Impact</div>
              <div className={cn("text-xl font-bold", result.totalImpact < 0 ? "text-red-400" : "text-emerald-400")}>
                {fmtCurrency(result.totalImpact)}
              </div>
            </GlassCard>
            <GlassCard className={cn("p-5 border-l-4", result.totalImpactPercent < 0 ? "border-l-red-500" : "border-l-emerald-500")}>
              <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Impact %</div>
              <div className={cn("text-xl font-bold flex items-center gap-2", result.totalImpactPercent < 0 ? "text-red-400" : "text-emerald-400")}>
                {result.totalImpactPercent < 0 ? <TrendingDown className="w-5 h-5" /> : <TrendingUp className="w-5 h-5" />}
                {fmtPct(result.totalImpactPercent)}
              </div>
            </GlassCard>
          </div>

          {/* Per-Asset-Class Breakdown */}
          <GlassCard className="p-6">
            <h3 className="text-sm font-bold text-slate-400 uppercase tracking-widest mb-4">Impact by Asset Class</h3>
            <div className="space-y-3">
              {(result.impacts || []).map((imp, i) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-slate-900/50 border border-white/5">
                  <div className="flex items-center gap-3">
                    <BarChart3 className="w-4 h-4 text-slate-500" />
                    <span className="text-sm font-semibold text-white">{imp.assetClass}</span>
                  </div>
                  <div className="flex items-center gap-8 text-sm">
                    <span className="text-slate-400">{fmtCurrency(imp.currentValue)}</span>
                    <span className="text-slate-500">→</span>
                    <span className="text-white">{fmtCurrency(imp.projectedValue)}</span>
                    <span className={cn("font-bold min-w-[80px] text-right", imp.impact < 0 ? "text-red-400" : "text-emerald-400")}>
                      {fmtPct(imp.impactPercent)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </GlassCard>
        </motion.div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Tab 2: Pre-Clear Approval Queue
// ---------------------------------------------------------------------------

const PreClearQueueTab: React.FC = () => {
  const [trades, setTrades] = useState<PreClearTrade[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [rejectingId, setRejectingId] = useState<number | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');

  const fetchPending = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/trade-transactions', { params: { status: 'PRECLEAR_PENDING' } });
      const list = res.data.content ?? res.data;
      setTrades(Array.isArray(list) ? list : []);
    } catch { setTrades([]); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchPending(); }, [fetchPending]);

  const approve = async (id: number) => {
    setActionLoading(id);
    try {
      await axios.post(`/api/pre-clear/${id}/approve`);
      fetchPending();
    } catch (e) { console.error('Approve failed', e); }
    setActionLoading(null);
  };

  const reject = async (id: number) => {
    if (!rejectionReason.trim()) return;
    setActionLoading(id);
    try {
      await axios.post(`/api/pre-clear/${id}/reject`, null, { params: { rejectionReason } });
      setRejectingId(null);
      setRejectionReason('');
      fetchPending();
    } catch (e) { console.error('Reject failed', e); }
    setActionLoading(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold text-white flex items-center gap-2">
          <Clock className="w-5 h-5 text-violet-400" />
          Pending Pre-Clear Approvals
          {trades.length > 0 && (
            <span className="ml-2 px-2 py-0.5 text-xs font-bold rounded-full bg-violet-500/20 text-violet-400 border border-violet-500/30">
              {trades.length}
            </span>
          )}
        </h3>
        <button onClick={fetchPending} className="p-2 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white transition-colors">
          <RefreshCw className={cn("w-4 h-4", loading && "animate-spin")} />
        </button>
      </div>

      {loading && trades.length === 0 ? (
        <GlassCard className="p-12 text-center text-slate-500">Loading pending trades...</GlassCard>
      ) : trades.length === 0 ? (
        <GlassCard className="p-12 text-center">
          <CheckCircle className="w-12 h-12 text-emerald-500/30 mx-auto mb-3" />
          <div className="text-slate-400 font-semibold">No pending pre-clear requests</div>
          <div className="text-xs text-slate-600 mt-1">All trades have been reviewed</div>
        </GlassCard>
      ) : (
        <div className="space-y-3">
          {trades.map(trade => (
            <GlassCard key={trade.id} className="p-5">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className={cn(
                      "px-2 py-0.5 text-xs font-bold rounded",
                      trade.transactionType === 'BUY'
                        ? "bg-emerald-500/20 text-emerald-400"
                        : "bg-red-500/20 text-red-400"
                    )}>
                      {trade.transactionType}
                    </span>
                    <span className="text-white font-semibold">{trade.securityName || `Security #${trade.securityId}`}</span>
                    <span className="text-slate-500 text-sm">→</span>
                    <span className="text-slate-400 text-sm">{trade.portfolioName || `Portfolio #${trade.portfolioId}`}</span>
                  </div>
                  <div className="flex items-center gap-6 text-sm text-slate-500">
                    <span>Amount: <span className="text-slate-300 font-semibold">{fmtCurrency(trade.amount || 0)}</span></span>
                    {trade.price && <span>Price: <span className="text-slate-300">{trade.price}</span></span>}
                    {trade.quantity && <span>Qty: <span className="text-slate-300">{trade.quantity?.toLocaleString()}</span></span>}
                    <span>Date: <span className="text-slate-300">{trade.tradeDate}</span></span>
                  </div>
                  {trade.notes && (
                    <div className="mt-2 text-xs text-slate-500 italic">"{trade.notes}"</div>
                  )}
                </div>

                <div className="flex items-center gap-2 ml-4">
                  {rejectingId === trade.id ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={rejectionReason}
                        onChange={e => setRejectionReason(e.target.value)}
                        placeholder="Rejection reason..."
                        className="px-3 py-1.5 bg-slate-900 border border-red-500/30 rounded-lg text-white text-sm w-64"
                        autoFocus
                      />
                      <button
                        onClick={() => reject(trade.id)}
                        disabled={!rejectionReason.trim() || actionLoading === trade.id}
                        className="px-3 py-1.5 bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white text-sm font-bold rounded-lg"
                      >
                        Confirm
                      </button>
                      <button
                        onClick={() => { setRejectingId(null); setRejectionReason(''); }}
                        className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-white text-sm rounded-lg"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <>
                      <button
                        onClick={() => approve(trade.id)}
                        disabled={actionLoading === trade.id}
                        className="px-4 py-1.5 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-sm font-bold rounded-lg flex items-center gap-1.5"
                      >
                        <CheckCircle className="w-3.5 h-3.5" /> Approve
                      </button>
                      <button
                        onClick={() => setRejectingId(trade.id)}
                        disabled={actionLoading === trade.id}
                        className="px-4 py-1.5 bg-red-600/20 hover:bg-red-600/40 border border-red-500/30 text-red-400 text-sm font-bold rounded-lg flex items-center gap-1.5"
                      >
                        <XCircle className="w-3.5 h-3.5" /> Reject
                      </button>
                    </>
                  )}
                </div>
              </div>
            </GlassCard>
          ))}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Tab 3: Override Workflow
// ---------------------------------------------------------------------------

const OverrideWorkflowTab: React.FC = () => {
  const [pendingOverrides, setPendingOverrides] = useState<OverrideRecord[]>([]);
  const [history, setHistory] = useState<OverrideRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [reviewingId, setReviewingId] = useState<number | null>(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [maxHours, setMaxHours] = useState(8);
  const [showHistory, setShowHistory] = useState(false);

  const fetchOverrides = useCallback(async () => {
    setLoading(true);
    try {
      const [pendingRes, historyRes] = await Promise.all([
        axios.get('/api/compliance/overrides/pending'),
        axios.get('/api/compliance/overrides/history', { params: { limit: 25 } })
      ]);
      setPendingOverrides(Array.isArray(pendingRes.data) ? pendingRes.data : []);
      setHistory(Array.isArray(historyRes.data) ? historyRes.data : []);
    } catch {
      setPendingOverrides([]);
      setHistory([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchOverrides(); }, [fetchOverrides]);

  const reviewOverride = async (id: number, approved: boolean) => {
    try {
      await axios.put(`/api/compliance/overrides/${id}/review`, {
        overrideId: id,
        approved,
        reviewNotes: reviewNotes || (approved ? 'Approved' : 'Rejected'),
        maxDurationHours: approved ? maxHours : null
      });
      setReviewingId(null);
      setReviewNotes('');
      setMaxHours(8);
      fetchOverrides();
    } catch (e) { console.error('Review failed', e); }
  };

  const statusColor = (status: string) => {
    switch (status) {
      case 'PENDING': return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
      case 'APPROVED': return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30';
      case 'REJECTED': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'EXPIRED': return 'bg-slate-500/20 text-slate-400 border-slate-500/30';
      default: return 'bg-slate-500/20 text-slate-400 border-slate-500/30';
    }
  };

  const renderOverride = (o: OverrideRecord, showActions: boolean) => (
    <GlassCard key={o.id} className="p-5">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <span className={cn("px-2 py-0.5 text-xs font-bold rounded border", statusColor(o.status))}>
              {o.status}
            </span>
            <span className="text-white font-semibold">Rule #{o.ruleId}</span>
            <span className="text-slate-500">|</span>
            <span className="text-slate-400 text-sm">Trade #{o.tradeId}</span>
            {o.orderId && <span className="text-slate-500 text-xs">Order: {o.orderId}</span>}
          </div>
          <div className="text-sm text-slate-400 mb-2">
            <span className="text-slate-500">Requested by</span>{' '}
            <span className="text-slate-300 font-semibold">{o.requestedBy}</span>{' '}
            <span className="text-slate-500">at</span>{' '}
            <span className="text-slate-300">{new Date(o.requestedAt).toLocaleString()}</span>
          </div>
          <div className="text-sm text-slate-300 italic mb-2">"{o.justification}"</div>
          <div className="flex items-center gap-6 text-xs text-slate-500">
            <span>Current: <span className="text-red-400 font-semibold">{o.currentValue?.toFixed(2)}{(o as any).unit === undefined ? '%' : (o as any).unit === '%' ? '%' : ` ${(o as any).unit}`}</span></span>
            <span>Limit: <span className="text-emerald-400 font-semibold">{o.limitValue?.toFixed(2)}{(o as any).unit === undefined ? '%' : (o as any).unit === '%' ? '%' : ` ${(o as any).unit}`}</span></span>
            <span>Breach: <span className="text-amber-400 font-semibold">{o.breachAmount?.toFixed(2)}{(o as any).unit === undefined ? '%' : (o as any).unit === '%' ? '%' : ` ${(o as any).unit}`}</span></span>
          </div>
          {o.reviewedBy && (
            <div className="mt-2 text-xs text-slate-500">
              Reviewed by <span className="text-slate-300">{o.reviewedBy}</span>{' '}
              at {new Date(o.reviewedAt!).toLocaleString()}
              {o.reviewNotes && <span className="italic"> — "{o.reviewNotes}"</span>}
              {o.expiresAt && <span className="ml-2 text-amber-400">Expires: {new Date(o.expiresAt).toLocaleString()}</span>}
            </div>
          )}
        </div>

        {showActions && o.status === 'PENDING' && (
          <div className="ml-4">
            {reviewingId === o.id ? (
              <div className="space-y-2 w-72">
                <input
                  type="text"
                  value={reviewNotes}
                  onChange={e => setReviewNotes(e.target.value)}
                  placeholder="Review notes..."
                  className="w-full px-3 py-1.5 bg-slate-900 border border-white/10 rounded-lg text-white text-sm"
                  autoFocus
                />
                <div className="flex items-center gap-2">
                  <label className="text-xs text-slate-500">Max hours:</label>
                  <input
                    type="number"
                    value={maxHours}
                    onChange={e => setMaxHours(parseInt(e.target.value) || 8)}
                    className="w-16 px-2 py-1 bg-slate-900 border border-white/10 rounded text-white text-sm"
                    min={1} max={24}
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => reviewOverride(o.id, true)}
                    className="flex-1 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-bold rounded-lg"
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => reviewOverride(o.id, false)}
                    className="flex-1 px-3 py-1.5 bg-red-600 hover:bg-red-500 text-white text-sm font-bold rounded-lg"
                  >
                    Reject
                  </button>
                  <button
                    onClick={() => setReviewingId(null)}
                    className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-white text-sm rounded-lg"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setReviewingId(o.id)}
                className="px-4 py-1.5 bg-amber-600 hover:bg-amber-500 text-white text-sm font-bold rounded-lg flex items-center gap-1.5"
              >
                <Eye className="w-3.5 h-3.5" /> Review
              </button>
            )}
          </div>
        )}
      </div>
    </GlassCard>
  );

  return (
    <div className="space-y-6">
      {/* Pending Overrides */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-400" />
            Pending Override Requests
            {pendingOverrides.length > 0 && (
              <span className="ml-2 px-2 py-0.5 text-xs font-bold rounded-full bg-amber-500/20 text-amber-400 border border-amber-500/30">
                {pendingOverrides.length}
              </span>
            )}
          </h3>
          <button onClick={fetchOverrides} className="p-2 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white transition-colors">
            <RefreshCw className={cn("w-4 h-4", loading && "animate-spin")} />
          </button>
        </div>

        {pendingOverrides.length === 0 ? (
          <GlassCard className="p-8 text-center">
            <CheckCircle className="w-10 h-10 text-emerald-500/30 mx-auto mb-2" />
            <div className="text-slate-400 font-semibold text-sm">No pending overrides</div>
          </GlassCard>
        ) : (
          <div className="space-y-3">{pendingOverrides.map(o => renderOverride(o, true))}</div>
        )}
      </div>

      {/* History */}
      <div>
        <button
          onClick={() => setShowHistory(!showHistory)}
          className="flex items-center gap-2 text-sm font-semibold text-slate-400 hover:text-white transition-colors"
        >
          {showHistory ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          Override History ({history.length})
        </button>
        {showHistory && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-3 space-y-3">
            {history.map(o => renderOverride(o, false))}
          </motion.div>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Tab 4: Regulatory Rules
// ---------------------------------------------------------------------------

interface ApplyTemplateResult {
  framework: string;
  ruleScope: string;
  applied: number;
  enabled: number;
  disabledNoEvaluator: number;
  skippedExisting: string[];
}

const RegulatoryRulesTab: React.FC = () => {
  const [framework, setFramework] = useState<string>('40ACT');
  const [rules, setRules] = useState<RegulatoryRule[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedRule, setExpandedRule] = useState<string | null>(null);
  // Apply-to-scope (Lane 6, F-CMP-004): persist the template for the firm, a group or a portfolio
  const [applyScope, setApplyScope] = useState<'FIRM' | 'PORTFOLIO_GROUP' | 'PORTFOLIO'>('PORTFOLIO_GROUP');
  const [groups, setGroups] = useState<{ id: number; groupName: string }[]>([]);
  const [portfolios, setPortfolios] = useState<{ id: number; portfolioName: string }[]>([]);
  const [targetId, setTargetId] = useState<string>('');
  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState<ApplyTemplateResult | null>(null);
  const [applyError, setApplyError] = useState<string | null>(null);

  const fetchRules = useCallback(async (fw: string) => {
    setLoading(true);
    try {
      const res = await axios.get(`/api/compliance-rules/templates/${fw}`);
      setRules(Array.isArray(res.data) ? res.data : []);
    } catch { setRules([]); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchRules(framework); setApplyResult(null); setApplyError(null); }, [framework, fetchRules]);

  useEffect(() => {
    axios.get('/api/portfolio-groups').then(r => setGroups(Array.isArray(r.data) ? r.data : [])).catch(() => setGroups([]));
    axios.get('/api/dimensions/portfolios').then(r => setPortfolios(Array.isArray(r.data) ? r.data : (r.data?.content ?? []))).catch(() => setPortfolios([]));
  }, []);

  const applyTemplate = async () => {
    setApplying(true); setApplyError(null); setApplyResult(null);
    try {
      const body: Record<string, unknown> = { framework, ruleScope: applyScope };
      if (applyScope === 'PORTFOLIO_GROUP') body.portfolioGroupId = Number(targetId);
      if (applyScope === 'PORTFOLIO') body.portfolioId = Number(targetId);
      const res = await axios.post('/api/compliance-rules/apply-template', body);
      setApplyResult(res.data);
    } catch (e: any) {
      setApplyError(e?.response?.data?.error || e?.response?.data?.message || 'Apply failed');
    }
    setApplying(false);
  };

  const governedCount = rules.filter(r => r.hasEvaluator).length;
  const targetMissing = applyScope !== 'FIRM' && !targetId;

  const breachColor = (action: string) => {
    switch (action) {
      case 'BLOCK': return 'bg-red-500/20 text-red-400';
      case 'WARN': return 'bg-amber-500/20 text-amber-400';
      case 'NOTIFY': return 'bg-blue-500/20 text-blue-400';
      case 'LOG': return 'bg-slate-500/20 text-slate-400';
      default: return 'bg-slate-500/20 text-slate-400';
    }
  };

  const severityColor = (sev: string) => {
    switch (sev) {
      case 'CRITICAL': return 'text-red-400';
      case 'HIGH': return 'text-amber-400';
      case 'MEDIUM': return 'text-blue-400';
      default: return 'text-slate-400';
    }
  };

  return (
    <div className="space-y-6">
      {/* Framework Selector */}
      <GlassCard className="p-6">
        <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
          <Scale className="w-5 h-5 text-blue-400" />
          Regulatory Framework
        </h3>
        <div className="flex gap-3">
          {[
            { key: '40ACT', label: '40 Act', desc: 'US mutual funds' },
            { key: 'ERISA', label: 'ERISA', desc: 'pension / 401k' },
            { key: 'UCITS', label: 'UCITS', desc: 'EU fund directive' }
          ].map(fw => (
            <button
              key={fw.key}
              onClick={() => setFramework(fw.key)}
              className={cn(
                "flex-1 p-4 rounded-xl border transition-all text-left",
                framework === fw.key
                  ? "bg-blue-500/10 border-blue-500/30 shadow-lg shadow-blue-500/5"
                  : "bg-slate-900/50 border-white/5 hover:border-white/10"
              )}
            >
              <div className={cn("text-sm font-bold mb-0.5", framework === fw.key ? "text-blue-400" : "text-slate-300")}>
                {fw.label}
              </div>
              <div className="text-xs text-slate-500">{fw.desc}</div>
            </button>
          ))}
        </div>
      </GlassCard>

      {/* Apply to scope (Lane 6) */}
      <GlassCard className="p-6">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Apply {framework} to</div>
            <select value={applyScope} onChange={e => { setApplyScope(e.target.value as any); setTargetId(''); }}
              className="bg-slate-900 border border-white/10 rounded-lg px-3 py-2 text-sm text-white">
              <option value="FIRM">Firm (all portfolios)</option>
              <option value="PORTFOLIO_GROUP">Portfolio group</option>
              <option value="PORTFOLIO">Single portfolio</option>
            </select>
          </div>
          {applyScope === 'PORTFOLIO_GROUP' && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Group</div>
              <select value={targetId} onChange={e => setTargetId(e.target.value)}
                className="bg-slate-900 border border-white/10 rounded-lg px-3 py-2 text-sm text-white min-w-[220px]">
                <option value="">Select a group…</option>
                {groups.map(g => <option key={g.id} value={g.id}>{g.groupName}</option>)}
              </select>
            </div>
          )}
          {applyScope === 'PORTFOLIO' && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Portfolio</div>
              <select value={targetId} onChange={e => setTargetId(e.target.value)}
                className="bg-slate-900 border border-white/10 rounded-lg px-3 py-2 text-sm text-white min-w-[220px]">
                <option value="">Select a portfolio…</option>
                {portfolios.map(p => <option key={p.id} value={p.id}>{p.portfolioName}</option>)}
              </select>
            </div>
          )}
          <button onClick={applyTemplate} disabled={applying || targetMissing || rules.length === 0}
            className="px-4 py-2 rounded-lg text-sm font-semibold bg-blue-600 hover:bg-blue-500 disabled:opacity-40 disabled:cursor-not-allowed text-white">
            {applying ? 'Applying…' : `Apply ${rules.length} rules`}
          </button>
          <div className="text-xs text-slate-400">
            {governedCount} of {rules.length} have a governed evaluator and will be enabled; the rest are inserted <span className="text-amber-400">disabled</span> and flagged.
          </div>
        </div>
        {applyResult && (
          <div className="mt-4 text-sm text-emerald-400">
            Applied {applyResult.applied} rules at {applyResult.ruleScope}: {applyResult.enabled} enabled, {applyResult.disabledNoEvaluator} disabled (no evaluator)
            {applyResult.skippedExisting?.length ? `, ${applyResult.skippedExisting.length} already present` : ''}.
          </div>
        )}
        {applyError && <div className="mt-4 text-sm text-red-400">{applyError}</div>}
      </GlassCard>

      {/* Rules List */}
      {loading ? (
        <GlassCard className="p-12 text-center text-slate-500">Loading rules...</GlassCard>
      ) : (
        <div className="space-y-3">
          {rules.map(rule => (
            <GlassCard key={rule.ruleKey} className="overflow-hidden">
              <button
                onClick={() => setExpandedRule(expandedRule === rule.ruleKey ? null : rule.ruleKey)}
                className="w-full p-5 flex items-center justify-between text-left hover:bg-white/[0.02] transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className={cn("w-2 h-2 rounded-full", rule.enabled ? "bg-emerald-400" : "bg-slate-600")} />
                  <div>
                    <div className="text-sm font-semibold text-white">{rule.ruleName}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{rule.ruleKey}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={cn("px-2 py-0.5 text-[10px] font-bold rounded", breachColor(rule.breachAction))}>
                    {rule.breachAction}
                  </span>
                  <span className={cn("text-xs font-semibold", severityColor(rule.severity))}>
                    {rule.severity}
                  </span>
                  <span className={cn("px-2 py-0.5 text-[10px] font-bold rounded", rule.hasEvaluator ? "bg-emerald-500/20 text-emerald-400" : "bg-amber-500/20 text-amber-400")}
                    title={rule.hasEvaluator ? 'Governed evaluator exists' : 'No evaluator: would fail closed if enabled'}>
                    {rule.hasEvaluator ? 'EVALUATOR' : 'NO EVALUATOR'}
                  </span>
                  {rule.thresholdValue && (
                    <span className="text-sm font-mono text-white bg-slate-800 px-2 py-0.5 rounded">
                      {rule.thresholdValue}
                    </span>
                  )}
                  {expandedRule === rule.ruleKey
                    ? <ChevronDown className="w-4 h-4 text-slate-500" />
                    : <ChevronRight className="w-4 h-4 text-slate-500" />}
                </div>
              </button>

              <AnimatePresence>
                {expandedRule === rule.ruleKey && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="border-t border-white/5"
                  >
                    <div className="p-5 bg-slate-900/30">
                      <div className="text-sm text-slate-300 mb-3">{rule.description}</div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                        <div>
                          <span className="text-slate-500">Type:</span>{' '}
                          <span className="text-slate-300 font-semibold">{rule.ruleType}</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Scope:</span>{' '}
                          <span className="text-slate-300 font-semibold">{rule.ruleScope}</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Source:</span>{' '}
                          <span className="text-slate-300 font-semibold">{rule.source}</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Status:</span>{' '}
                          <span className={cn("font-semibold", rule.enabled ? "text-emerald-400" : "text-slate-500")}>
                            {rule.enabled ? 'Enabled' : 'Disabled'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </GlassCard>
          ))}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

type TabType = 'whatif' | 'preclear' | 'overrides' | 'rules';

const ComplianceDashboardScreen: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('whatif');
  const [pendingCount, setPendingCount] = useState(0);
  const [overrideCount, setOverrideCount] = useState(0);

  useEffect(() => {
    // Fetch badge counts
    axios.get('/api/trade-transactions', { params: { status: 'PRECLEAR_PENDING' } })
      .then(res => {
        const list = res.data.content ?? res.data;
        setPendingCount(Array.isArray(list) ? list.length : 0);
      }).catch(() => {});

    axios.get('/api/compliance/overrides/pending')
      .then(res => setOverrideCount(Array.isArray(res.data) ? res.data.length : 0))
      .catch(() => {});
  }, [activeTab]);

  return (
    <Container>
      <TabsContainer>
        <div className="max-w-[1800px] mx-auto mb-4">
          <h1 className="text-2xl font-bold text-white tracking-tight flex items-center gap-3">
            <Shield className="w-7 h-7 text-amber-400" />
            Compliance Engine
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            What-if scenarios, pre-trade validation, override workflow, and regulatory rule management
          </p>
        </div>
        <TabsWrapper>
          <ComplianceTab
            icon={<Zap className="w-4 h-4" />}
            label="What-If Scenarios"
            active={activeTab === 'whatif'}
            onClick={() => setActiveTab('whatif')}
          />
          <ComplianceTab
            icon={<Clock className="w-4 h-4" />}
            label="Pre-Clear Queue"
            badge={pendingCount}
            active={activeTab === 'preclear'}
            onClick={() => setActiveTab('preclear')}
          />
          <ComplianceTab
            icon={<AlertTriangle className="w-4 h-4" />}
            label="Override Workflow"
            badge={overrideCount}
            active={activeTab === 'overrides'}
            onClick={() => setActiveTab('overrides')}
          />
          <ComplianceTab
            icon={<Scale className="w-4 h-4" />}
            label="Regulatory Rules"
            active={activeTab === 'rules'}
            onClick={() => setActiveTab('rules')}
          />
        </TabsWrapper>
      </TabsContainer>

      <ContentContainer>
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            {activeTab === 'whatif' && <WhatIfTab />}
            {activeTab === 'preclear' && <PreClearQueueTab />}
            {activeTab === 'overrides' && <OverrideWorkflowTab />}
            {activeTab === 'rules' && <RegulatoryRulesTab />}
          </motion.div>
        </AnimatePresence>
      </ContentContainer>
    </Container>
  );
};

export default ComplianceDashboardScreen;
